// Property binding metadata type
// Symbol for storing property bindings
const PROPERTY_BINDINGS = Symbol('propertyBindings');
const MODEL_BINDINGS = Symbol('modelBindings');
const VALUES = Symbol('values');


type BindingType = 'textContent' | 'attribute' | 'toggleClass' | 'style' | 'html';

type BindingMetadata = {
  type          : BindingType;
  querySelector : string;
  param?        : string; // Generic parameter for attribute name, class name, or style property
};

export abstract class BaseModel{  
  
  protected parent : any = document; 
  
  constructor(parentElement?:HTMLElement){
    if (parentElement) {
      this.parent = parentElement; 
    }
  }

  get Parent(){
    return this.parent;
  }
  
  set Parent(parentElement:HTMLElement){
    this.parent = parentElement;
  }
}

// Generic binding decorator factory
function createBindingDecorator(type: BindingType, param?: string) {
  return function(querySelector: string, additionalParam?: string) {
    return function(target: any, propertyKey: string) {
      if (!target.constructor[PROPERTY_BINDINGS]) {
        target.constructor[PROPERTY_BINDINGS] = new Map<string, BindingMetadata>();
      }
      target.constructor[PROPERTY_BINDINGS].set(propertyKey, {
        type,
        querySelector,
        param: additionalParam || param
      });
    };
  };
}

// Exported binding decorators
export const bindTextContent = createBindingDecorator('textContent');
export const bindAttribute = (querySelector: string, attributeName: string) => 
  createBindingDecorator('attribute')(querySelector, attributeName);
export const bindToggleClass = (querySelector: string, cssClassName: string) => 
  createBindingDecorator('toggleClass')(querySelector, cssClassName);
export const bindStyle = (querySelector: string, styleAttributeName: string) => 
  createBindingDecorator('style')(querySelector, styleAttributeName);
export const bindHtml = (parentQuerySelector: string, html: string) => 
  createBindingDecorator('html')(parentQuerySelector, html);

function createModelBindingDecorator(templateSelector?:string) {  
      return function <T extends { new(...args: any[]): {} }>(constructor: T) {
        if (!constructor.prototype[MODEL_BINDINGS]) {
            constructor.prototype[MODEL_BINDINGS] = new Map<string, string>();
        }
        // No selector ?
        if ( !templateSelector ) return;
        // Set parent selector                
        constructor.prototype[MODEL_BINDINGS].set(constructor.name, templateSelector);        
      }      
}

export const bindModel1 = createModelBindingDecorator;


// Class decorator that adds getters/setters with auto-binding
export function bindModel(parentSelector?: string) {
  return function bindModel<T extends { new(...args: any[]): BaseModel}>(constructor: T) {
  
    const bindings: Map<string, BindingMetadata> = constructor.prototype.constructor[PROPERTY_BINDINGS] || new Map();  
      // Store private values
      const VALUES = Symbol('values');
      
      if (!constructor.prototype[MODEL_BINDINGS]) {
        constructor.prototype[MODEL_BINDINGS] = new Map<string, string>();
      }
      
        // Set parent selector
      console.log( "deco model : set parent selector " + constructor.name);
      constructor.prototype[MODEL_BINDINGS].set(constructor.name, parentSelector);    

      return class extends constructor {
        private [VALUES]: Map<string, any> = new Map();
        private className : string = constructor.name;

        constructor(...args: any[]) {
            super(...args);
        
            // if template create template and append to parent or document
            if ( parentSelector ){
              let htmlTemplate = this.parent.querySelector(parentSelector) as HTMLTemplateElement;
              if (htmlTemplate){
                const clone  = htmlTemplate.content.cloneNode(true);                
                // update parent to this template
                this.Parent = clone as HTMLElement;
              }
            }   
              // Create getters and setters for each bound property
              bindings.forEach((metadata, propertyKey) => {
                Object.defineProperty(this, propertyKey, {
                  get: function() {
                    return this[VALUES].get(propertyKey);
                  },
                  set: function(value: any) {
                    this[VALUES].set(propertyKey, value);
                    this._updateDOM(propertyKey, value, metadata);
                  },
                  enumerable: true,
                  configurable: true
                });
        });
        }
        
        get ClassName() {
          return this.className;
        }

        private _updateDOM(propertyKey: string, value: any, metadata: BindingMetadata) {
        const element = (this.Parent).querySelector(metadata.querySelector);
          if (!element) {
            console.warn(`Element not found for selector: ${metadata.querySelector}`);
            return;
          }
      
        switch (metadata.type) {
          case 'textContent':
            element.textContent = String(value);
            break;
          
          case 'attribute':
            if (metadata.param) {
              element.setAttribute(metadata.param, String(value));
            }
            break;
          
          case 'toggleClass':
            if (metadata.param) {
              element.classList.toggle(metadata.param, Boolean(value));
            }
          break;
          
          case 'style':
            if (metadata.param) {
              (element as HTMLElement).style.setProperty(metadata.param, String(value));
            }
          break;
      }
    }
  } as T;
}
}